/**
 * Task #2917 — Anagrafica PA persistente: tipi/shape condivisi della Pubblica
 * Amministrazione come TERZA tipologia di cessionario (accanto a B2C/B2B).
 *
 * Fonte di verità per il frontend (selettore cessionario), il backend (CRUD +
 * ricerca, validazione boundary) e il mapper e-invoice. Il campo distintivo è
 * `codice_ipa`: il Codice Univoco Ufficio IPA a 6 caratteri (CodiceDestinatario
 * PA del tracciato FPA12), vs i 7 caratteri del Codice SDI privato (B2B).
 *
 * CIG/CUP NON appartengono all'anagrafica: restano riferimenti a livello
 * DOCUMENTO (DatiOrdineAcquisto/DatiContratto), già gestiti dal mapper.
 */

import { z } from 'zod';
import {
  isValidItalianVat,
  isValidItalianFiscalCode,
  isValidCap,
  isValidProvince,
  isValidEmail,
} from '../validators/italian-fiscal';

/** Lunghezza esatta del CodiceDestinatario di una PA (Codice Univoco Ufficio IPA). */
export const PA_CODICE_DESTINATARIO_LENGTH = 6;

const CODICE_IPA_RE = /^[A-Z0-9]{6}$/;

/** Verifica formale del Codice Univoco Ufficio IPA: 6 alfanumerici maiuscoli. */
export function isValidCodiceIpa(code?: string | null): boolean {
  return CODICE_IPA_RE.test((code ?? '').trim().toUpperCase());
}

/**
 * Tipologia di cessionario fattura. La PA è la terza, distinta da B2C/B2B:
 * cambia il blocco anagrafico e il CodiceDestinatario (6 vs 7 char) → FPA12.
 */
export type CessionarioKind = 'b2c' | 'b2b' | 'pa';

/**
 * Riga anagrafica PA (shape API, snake_case, mirror della tabella
 * `public.crm_pa_offices`).
 */
export interface PaOffice {
  id: string;
  tenant_id: string;
  denominazione: string;
  /** Codice Univoco Ufficio IPA, 6 caratteri (= CodiceDestinatario PA). */
  codice_ipa: string;
  codice_fiscale: string | null;
  partita_iva: string | null;
  indirizzo: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  nazione: string;
  pec_email: string | null;
  note: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Schemi Zod per il boundary HTTP (create/update)
// ---------------------------------------------------------------------------

const trimmedRequired = (max: number) =>
  z.string().trim().min(1).max(max);

const trimmedOptional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable()
    .optional();

const codiceIpaSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine(isValidCodiceIpa, {
    message: 'Codice Univoco Ufficio IPA non valido (6 caratteri alfanumerici).',
  });

const codiceFiscaleSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine(isValidItalianFiscalCode, { message: 'Codice fiscale non valido.' })
  .nullable()
  .optional();

const partitaIvaSchema = z
  .string()
  .trim()
  .refine(isValidItalianVat, { message: 'Partita IVA non valida.' })
  .nullable()
  .optional();

const capSchema = z
  .string()
  .trim()
  .refine((v) => v.length === 0 || isValidCap(v), { message: 'CAP non valido.' })
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional();

const provinciaSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => v.length === 0 || isValidProvince(v), {
    message: 'Sigla provincia non valida.',
  })
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional();

const pecSchema = z
  .string()
  .trim()
  .refine((v) => v.length === 0 || isValidEmail(v), { message: 'PEC non valida.' })
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional();

const nazioneSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(2, 'Codice nazione ISO a 2 lettere.')
  .default('IT');

/** Schema creazione anagrafica PA (POST). */
export const PaOfficeCreateSchema = z.object({
  denominazione: trimmedRequired(255),
  codice_ipa: codiceIpaSchema,
  codice_fiscale: codiceFiscaleSchema,
  partita_iva: partitaIvaSchema,
  indirizzo: trimmedOptional(255),
  cap: capSchema,
  comune: trimmedOptional(120),
  provincia: provinciaSchema,
  nazione: nazioneSchema,
  pec_email: pecSchema,
  note: trimmedOptional(2000),
  is_active: z.boolean().optional(),
});
export type PaOfficeCreateInput = z.infer<typeof PaOfficeCreateSchema>;

/** Schema aggiornamento parziale (PATCH); almeno un campo presente. */
export const PaOfficeUpdateSchema = PaOfficeCreateSchema.partial().refine(
  (obj) => Object.keys(obj).length > 0,
  { message: 'Nessun campo da aggiornare.' },
);
export type PaOfficeUpdateInput = z.infer<typeof PaOfficeUpdateSchema>;

/** Query di ricerca lista PA. */
export const PaOfficeSearchQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  active_only: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v !== 'false'),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export type PaOfficeSearchQuery = z.infer<typeof PaOfficeSearchQuerySchema>;

// ---------------------------------------------------------------------------
// Mappatura cessionario PA → blocco customer fattura
// ---------------------------------------------------------------------------

/** Indirizzo cessionario nel formato consumato dal mapper e-invoice. */
export interface CessionarioAddress {
  address: string | null;
  postal_code: string | null;
  city: string | null;
  province: string | null;
  country: string;
}

/**
 * Blocco cessionario per la creazione fattura. `sdi` porta SEMPRE il
 * CodiceDestinatario: per la PA è il `codice_ipa` a 6 caratteri, da cui il
 * mapper inferisce il profilo PA (FPA12, esigibilità "S", CIG/CUP).
 */
export interface InvoiceCessionario {
  kind: CessionarioKind;
  id: string | null;
  /**
   * Denominazione (B2B/PA) o nominativo composto "Nome Cognome" (B2C). Resta il
   * valore canonico di `invoices.customer_name`/display/ricerca.
   */
  name: string;
  /** Persona fisica (B2C): Nome separato per l'Anagrafica FatturaPA. */
  first_name?: string | null;
  /** Persona fisica (B2C): Cognome separato per l'Anagrafica FatturaPA. */
  last_name?: string | null;
  /** Forma giuridica (B2B), catalogo `public.legal_forms`. Solo CRM/display. */
  legal_form?: string | null;
  vat: string | null;
  cf: string | null;
  sdi: string | null;
  pec: string | null;
  address: CessionarioAddress | null;
}

/**
 * Converte un'anagrafica PA nel blocco cessionario fattura. Il `codice_ipa`
 * (6 char) confluisce in `sdi`: è ciò che attiva il profilo PA nel mapper
 * e-invoice (`resolveActiveProfiles` rileva la PA quando `sdi.length === 6`).
 */
export function paOfficeToCessionario(pa: PaOffice): InvoiceCessionario {
  const hasAddress =
    !!pa.indirizzo || !!pa.cap || !!pa.comune || !!pa.provincia;
  return {
    kind: 'pa',
    id: pa.id,
    name: pa.denominazione,
    vat: pa.partita_iva,
    cf: pa.codice_fiscale,
    sdi: pa.codice_ipa,
    pec: pa.pec_email,
    address: hasAddress
      ? {
          address: pa.indirizzo,
          postal_code: pa.cap,
          city: pa.comune,
          province: pa.provincia,
          country: pa.nazione,
        }
      : null,
  };
}
